import { Link } from "react-router-dom";
import { fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { EthicalAgreementDialog } from "@/components/EthicalAgreementDialog";
import { Loader2, Send, Save } from "lucide-react";

export const OfferForm = ({
  screening, offerAmount, setOfferAmount, note, setNote,
  agreed, setAgreed, sendBusy, savingDraft, onSend, onSaveDraft,
}) => (
  <Card className="rounded-2xl">
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-base">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">2</span>
        Make your offer
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="offer-amount">Offer amount (USD) — paid upfront</Label>
          <Input data-testid="booking-offer-amount-input" id="offer-amount" type="number" min="1"
            value={offerAmount} onChange={(e) => setOfferAmount(e.target.value)} className="rounded-xl bg-card" />
          <p className="text-[11px] text-muted-foreground">AI suggested {fmtMoney(screening.rate_suggestion?.suggested_total)} — the model can accept, decline or counter.</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="offer-note">Note to the model (optional)</Label>
          <Textarea data-testid="booking-offer-note-input" id="offer-note" rows={2} value={note}
            onChange={(e) => setNote(e.target.value)} className="rounded-xl bg-card" placeholder="Anything they should know…" />
        </div>
      </div>
      <div className="flex items-start gap-3 rounded-xl border-2 border-[hsl(174,38%,70%)] bg-[hsl(174,38%,96%)] p-4">
        <Checkbox data-testid="booking-agreement-checkbox" id="agree" checked={agreed} onCheckedChange={setAgreed} className="mt-0.5" />
        <label htmlFor="agree" className="text-sm leading-snug">
          I accept the{" "}
          <EthicalAgreementDialog
            trigger={
              <button type="button" data-testid="booking-agreement-link" className="font-semibold text-[hsl(174,38%,30%)] underline">
                LOOKBOOK Ethical Usage Agreement
              </button>
            }
          />{" "}
          — no X-rated usage, no serious alterations to the model's look, retouching only with consent, usage
          strictly limited to the media channels declared above, the approved scope, and the licence period.
        </label>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button data-testid="booking-send-offer-button" onClick={onSend} disabled={sendBusy || !agreed} size="lg"
          className="rounded-xl shadow-[0_10px_25px_rgba(30,26,22,0.12)] transition-[box-shadow] hover:shadow-[0_14px_35px_rgba(30,26,22,0.16)]">
          {sendBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
          Send booking request
        </Button>
        <Button data-testid="booking-save-draft-offer-button" type="button" variant="outline" size="lg" disabled={savingDraft}
          className="rounded-xl border bg-card hover:bg-muted" onClick={onSaveDraft}>
          {savingDraft ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Save as draft
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Nothing is charged yet — you pay from your <Link to="/wallet" className="underline">wallet</Link> only after the model consents.
      </p>
    </CardContent>
  </Card>
);
